import { useEffect, useRef, useState } from "react";
import { Mic, MicOff } from "lucide-react";

// Web Speech API dictation button.
// Works on Chrome, Edge, Safari (recent), Chrome Android. Not Firefox.
export default function VoiceMic({ value, onChange, testIdSuffix }) {
  const [supported, setSupported] = useState(true);
  const [recording, setRecording] = useState(false);
  const [error, setError] = useState(null);
  const recRef = useRef(null);
  const baseRef = useRef("");

  useEffect(() => {
    const SR =
      typeof window !== "undefined" &&
      (window.SpeechRecognition || window.webkitSpeechRecognition);
    if (!SR) {
      setSupported(false);
      return;
    }
    const rec = new SR();
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = "en-US";

    rec.onresult = (e) => {
      let finalText = "";
      let interim = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const t = e.results[i][0].transcript;
        if (e.results[i].isFinal) finalText += t;
        else interim += t;
      }
      const combined = (baseRef.current + " " + finalText + " " + interim)
        .replace(/\s+/g, " ")
        .trimStart();
      onChange(combined);
      if (finalText) baseRef.current = (baseRef.current + " " + finalText).replace(/\s+/g, " ").trimStart();
    };
    rec.onerror = (e) => {
      setError(e.error || "mic-error");
      setRecording(false);
    };
    rec.onend = () => setRecording(false);

    recRef.current = rec;
    return () => {
      try { rec.stop(); } catch (_) {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggle = () => {
    if (!recRef.current) return;
    if (recording) {
      try { recRef.current.stop(); } catch (_) {}
      setRecording(false);
      return;
    }
    setError(null);
    baseRef.current = (value || "").trim();
    try {
      recRef.current.start();
      setRecording(true);
    } catch (err) {
      setError("start-failed");
    }
  };

  if (!supported) {
    return (
      <button
        type="button"
        disabled
        title="Voice input not supported in this browser. Use Chrome, Edge, or Safari."
        data-testid={`mic-unsupported-${testIdSuffix}`}
        className="p-1.5 rounded-md text-[var(--pa-text-dim)] cursor-not-allowed"
      >
        <MicOff size={14} />
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={toggle}
      title={recording ? "Stop dictation" : "Start voice dictation"}
      aria-pressed={recording}
      data-testid={`mic-${testIdSuffix}`}
      className={`relative p-1.5 rounded-md transition-colors ${
        recording
          ? "text-red-400 bg-red-500/10"
          : "text-[var(--pa-text-muted)] hover:text-[var(--pa-gold)] hover:bg-[var(--pa-gold)]/10"
      }`}
    >
      <Mic size={14} />
      {recording && (
        <>
          <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-red-500 animate-pulse" />
          <span className="absolute inset-0 rounded-md ring-2 ring-red-500/40 animate-ping" />
        </>
      )}
      {error && (
        <span className="sr-only" data-testid={`mic-error-${testIdSuffix}`}>{error}</span>
      )}
    </button>
  );
}
